import type { CatalogEntry } from '../types';

/**
 * Apus (feature `21`) error codes + catalog. Apus is Saleshandy's internal
 * admin panel — a server-to-server caller, not a human `apps/admin` surface —
 * so it sits in the low band beside `Saleshandy` (`20`) rather than in `90+`.
 *
 * One band spans both apps (`apps/admin`'s read-only surface and `apps/api`'s
 * login-token route) rather than splitting between `90` and `01`: the band
 * names the CALLER, which is what an operator reading a code needs to know.
 *
 * Code shape is `21AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * Endpoint → `AA` map:
 *   shared=00 (cross-endpoint, incl. `ApusTokenGuard`),
 *   organizationsList=01 (POST /admin/apus/organizations/list),
 *   organizationDetails=02 (GET /admin/apus/organizations/:organizationId),
 *   emailAccountsList=04 (POST /admin/apus/organizations/:organizationId/email-accounts/list),
 *   loginToken=03 (POST /internal/apus/login-token),
 *   planChange=05 (POST /internal/apus/organizations/:organizationId/plan-change),
 *   extension=06 (POST /internal/apus/organizations/:organizationId/extensions).
 *
 * The `05`/`06` messages are what APUS shows as an approval request's FAILED
 * reason, so they describe the rule that failed in customer-neutral words and
 * never name an org, a support user or a Paddle id.
 *
 * `01` and `02` are RESERVED for those two endpoints and currently hold no
 * codes — both answer only with `Shared.*` conditions today. Do NOT reuse
 * either `AA` for a different endpoint; allocate the next free one instead.
 *
 * See docs/plans/2026-09-11-apus-admin-panel-integration.md §4.4 and
 * docs/plans/2026-09-17-apus-plan-change-and-extensions.md.
 */
export const APUS_CODES = {
  /** Cross-endpoint conditions (shared=00). */
  Shared: {
    /** `ApusTokenGuard` rejected the call — header missing, or the shared secret did not match. */
    Unauthorized: '210001',
    /** The `organizationId` in the path/body resolves to no organization. */
    OrganizationNotFound: '210002',
    /**
     * Retired 2026-10-15 — self-approval is allowed; nothing throws this. Kept so
     * the number is never reused for a different meaning.
     */
    SelfApproval: '210003',
    /** The org is no longer on the plan the request was made from (`fromPlanId`). */
    PlanChangedSinceRequest: '210004',
    /** The org has no subscription row — a should-never-happen data state post-backfill. */
    SubscriptionNotFound: '210005',
  },
  /** loginToken=03 (POST /internal/apus/login-token). */
  LoginToken: {
    /** No user holds the requested email address. */
    UserNotFound: '210301',
    /**
     * The user exists but belongs to no organization, so there is no workspace
     * to log the operator into. A data state rather than a caller error —
     * every normal signup path creates a membership.
     */
    NoOrgMembership: '210302',
  },
  /** planChange=05 (POST /internal/apus/organizations/:organizationId/plan-change). */
  PlanChange: {
    /** The target plan is not one of the 18 current catalog plans, Free or Trial. */
    PlanNotAllowed: '210501',
    /** `startDate` is after today (UTC). */
    StartDateInFuture: '210502',
    /** `endDate` is before today (UTC). */
    EndDateInPast: '210503',
    /**
     * A non-free target was sent without an `endDate`. (No separate "end before
     * start" code: start ≤ today ≤ end already implies it.)
     */
    EndDateRequired: '210504',
    /** A paid target was sent without both Paddle ids. */
    PaddleIdsRequired: '210505',
    /** Another org's subscription row already carries this `paddleSubscriptionId`. */
    PaddleSubscriptionInUse: '210506',
    /** Paddle's `custom_data.orgId` on the subscription names a different org. */
    PaddleLinkedToOtherOrg: '210507',
    /** Paddle's GET or the `custom_data` PATCH failed — nothing was written. */
    PaddleLinkFailed: '210508',
    /** The Paddle subscription belongs to a different Paddle customer than the one on the request. */
    PaddleCustomerMismatch: '210509',
  },
  /** extension=06 (POST /internal/apus/organizations/:organizationId/extensions). */
  Extension: {
    /** The org's plan is Free or Trial — extensions need a paid/legacy plan. */
    NotEligible: '210601',
    /** The action needs a field (`featureCode`, `value`, `item`) the request did not carry. */
    MissingActionField: '210602',
    /** The plan's limit is already unlimited, so there is nothing to raise. */
    PlanLimitUnlimited: '210603',
    /** The requested limit is below the plan's own value (extensions are add-only). */
    LimitBelowPlan: '210604',
    /** The requested limit is above the allowed maximum. */
    LimitAboveMax: '210605',
    /** The feature key / permission is not one TrulyInbox knows. */
    UnknownItem: '210606',
    /** The plan already includes the item — there is nothing to unlock. */
    ItemNotLocked: '210607',
    /** The permission is not one support can unlock (no current catalog plan grants it). */
    ItemNotUnlockable: '210608',
    /** The item was not unlocked by support, so it cannot be removed. */
    ItemNotSupportUnlocked: '210609',
    /** The item is already unlocked by support — there is nothing to unlock. */
    AlreadyUnlocked: '210610',
    /** `rewardClaimed: true` was sent on an action other than `set-limit` — the review reward IS a limit increase. */
    RewardClaimRequiresSetLimit: '210611',
  },
} as const;

export const APUS_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '210001': { httpStatus: 401, message: 'This request could not be authorized.' },
  '210002': { httpStatus: 404, message: "We couldn't find that organization." },
  '210003': {
    httpStatus: 422,
    message: 'The requester cannot approve their own request.',
  },
  '210004': {
    httpStatus: 409,
    message: 'The organization is no longer on the plan this request was made from.',
  },
  '210005': { httpStatus: 404, message: 'The organization has no subscription.' },

  // ── Login token ──
  '210301': { httpStatus: 404, message: "We couldn't find a user with that email address." },
  '210302': {
    httpStatus: 409,
    message: "That user isn't a member of any organization yet.",
  },

  // ── Plan change ──
  '210501': { httpStatus: 422, message: 'That plan cannot be assigned from here.' },
  '210502': { httpStatus: 422, message: 'The start date cannot be in the future.' },
  '210503': { httpStatus: 422, message: 'The end date has already passed.' },
  '210504': { httpStatus: 422, message: 'An end date is required for this plan.' },
  '210505': {
    httpStatus: 422,
    message: 'A Paddle customer id and subscription id are required for this plan.',
  },
  '210506': {
    httpStatus: 409,
    message: 'That Paddle subscription is already used by another organization.',
  },
  '210507': {
    httpStatus: 409,
    message: 'That Paddle subscription is linked to another organization.',
  },
  '210508': {
    httpStatus: 502,
    message: 'Paddle could not be updated. Nothing was changed; please try again.',
  },
  '210509': {
    httpStatus: 422,
    message: 'That Paddle subscription belongs to a different Paddle customer.',
  },

  // ── Extension ──
  '210601': {
    httpStatus: 422,
    message: 'Extensions are not available on the Free or Trial plan.',
  },
  '210602': { httpStatus: 422, message: 'The request is missing a field this action needs.' },
  '210603': {
    httpStatus: 422,
    message: 'The plan already allows an unlimited value for this limit.',
  },
  '210604': { httpStatus: 422, message: "The new limit cannot be lower than the plan's own limit." },
  '210605': { httpStatus: 422, message: 'The new limit is above the allowed maximum.' },
  '210606': { httpStatus: 422, message: "That feature or permission doesn't exist." },
  '210607': { httpStatus: 422, message: 'The plan already includes that feature or permission.' },
  '210608': { httpStatus: 422, message: 'That permission cannot be unlocked by support.' },
  '210609': {
    httpStatus: 422,
    message: 'That feature or permission was not unlocked by support.',
  },
  '210610': {
    httpStatus: 422,
    message: 'That feature or permission is already unlocked by support.',
  },
  '210611': {
    httpStatus: 422,
    message: 'A review reward can only be marked as claimed together with a limit increase.',
  },
};
